import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Slider,
} from '@mui/material';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { selectActiveZoomId, zoomReset } from 'features/content-zoom/contentZoomSlice';
import Icon from 'components/Icon';
import Tooltip from 'components/Tooltip';
import { useBreakpoints } from 'hooks/useBreakpoints';
import Menu from 'components/Menu/Menu';
import { getTldrawSync } from 'utils/whiteboard/tldrawRuntime';
import { useTranslation } from 'react-i18next';
import { useContentBoard } from 'features/content-whiteboard/hooks/useContentBoard';
import { useContentBoardPanelControl } from 'features/content-whiteboard/hooks/useContentBoardPanelControl';
import {
  ContentBoardAvailablePanelTool,
  ContentBoardColor,
  ContentBoardFill,
  ContentBoardTool,
} from 'features/content-whiteboard/types';
import {
  CONTENT_BOARD_COLORS,
  CONTENT_BOARD_COLORS_MAP,
  CONTENT_BOARD_FILLS,
  CONTENT_BOARD_FILL_ICONS_MAP,
  CONTENT_BOARD_FILL_SHAPE_TYPES,
  CONTENT_BOARD_FILL_TOOLS,
  CONTENT_BOARD_PANEL_ICONS_MAP,
  CONTENT_BOARD_AVAILABLE_TOOLS_MAP,
  CONTENT_BOARD_BG_SHAPE_ID,
  CONTENT_BOARD_TOOLS_MAP,
} from 'features/content-whiteboard/constants';
import { useContentWhiteboardContext } from 'features/content-whiteboard/context/ContentWhiteboardContext';
import { usePiPWindow } from 'components/PiP/PiPProvider';

const ControlContainer = styled(Button)(({ theme }) => ({
  position: 'relative',
  gap: theme.spacing(1),
  width: 72,
  height: 32,
  borderRadius: 32 / 2,
  backgroundColor: alpha(theme.palette.common.black, 0.6),
  overflow: 'hidden',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
}));

const ContentWhiteboardPanelControl = () => {
  const { t } = useTranslation('content-whiteboard');

  const { pipWindow } = usePiPWindow();

  const [open, setOpen] = useState(false);

  const {
    currentEditor,
    currentTool,
    currentColor,
    currentOpacity,
    currentFill,
    setCurrentTool,
    setCurrentColor,
    setCurrentOpacity,
    setCurrentFill,
  } = useContentWhiteboardContext();

  const currentOpacityRef = useRef(currentOpacity);
  const currentColorRef = useRef(currentColor);
  const currentToolRef = useRef(currentTool);
  const currentFillRef = useRef(currentFill);

  const [selectionHasFill, setSelectionHasFill] = useState(false);

  const currentSelectedShapes = useRef('');

  const contentBoard = useContentBoard();

  const stylePanelId = useId();

  const { tooltip } = useContentBoardPanelControl();

  const activeZoomId = useAppSelector(selectActiveZoomId);
  const dispatch = useAppDispatch();

  const { isMobile } = useBreakpoints();

  const toggleMenu = () => {
    setOpen(!open);
    if (activeZoomId) {
      dispatch(zoomReset({ zoomId: activeZoomId }));
    }
  };
  const closeMenu = () => setOpen(false);

  const selectGeo = useCallback(
    (figure: 'ellipse' | 'rectangle') => {
      if (!contentBoard.editor) {
        return;
      }

      try {
        contentBoard.editor.batch(() => {
          if (contentBoard.editor) {
            contentBoard.editor.setOpacityForNextShapes(currentOpacityRef.current);
            contentBoard.editor.setCurrentTool('geo');
            const { GeoShapeGeoStyle } = getTldrawSync();
            contentBoard.editor.setStyleForNextShapes(GeoShapeGeoStyle, figure);
          }
        });
      } catch {
        /* do nothing */
      }
    },
    [contentBoard.editor]
  );

  const handleColor = useCallback(
    (color: ContentBoardColor) => {
      if (!contentBoard.editor) {
        return;
      }

      try {
        const { DefaultColorStyle } = getTldrawSync();
        contentBoard.editor.batch(() => {
          if (contentBoard.editor) {
            contentBoard.editor.setStyleForNextShapes(DefaultColorStyle, color);
            contentBoard.editor.setStyleForSelectedShapes(DefaultColorStyle, color);
          }
        });

        currentColorRef.current = color;

        setCurrentColor(color);
      } catch {
        /* do nothing */
      }
    },
    [setCurrentColor, contentBoard.editor]
  );

  /**
   * Keeps the panel honest: whatever fill the panel highlights is the fill the next shape gets,
   * so adopting a selected shape's fill has to reach the editor too, not just the panel state.
   */
  const adoptFill = useCallback(
    (fill: ContentBoardFill) => {
      if (!contentBoard.editor) {
        return;
      }

      try {
        const { DefaultFillStyle } = getTldrawSync();
        contentBoard.editor.setStyleForNextShapes(DefaultFillStyle, fill);

        currentFillRef.current = fill;

        setCurrentFill(fill);
      } catch {
        /* do nothing */
      }
    },
    [setCurrentFill, contentBoard.editor]
  );

  const handleFill = useCallback(
    (fill: ContentBoardFill) => {
      if (!contentBoard.editor) {
        return;
      }

      try {
        const { DefaultFillStyle } = getTldrawSync();
        contentBoard.editor.batch(() => {
          if (contentBoard.editor) {
            contentBoard.editor.setStyleForSelectedShapes(DefaultFillStyle, fill);
            adoptFill(fill);
          }
        });
      } catch {
        /* do nothing */
      }
    },
    [adoptFill, contentBoard.editor]
  );

  const handleOpacity = useCallback(
    (opacity: number) => {
      if (!contentBoard.editor) {
        return;
      }

      contentBoard.editor.batch(() => {
        if (contentBoard.editor) {
          const nextOpacity =
            currentToolRef.current === 'highlight' ? Math.max(opacity * 0.5, 0) : opacity;
          contentBoard.editor.setOpacityForNextShapes(nextOpacity);
          contentBoard.editor.setOpacityForSelectedShapes(nextOpacity);
        }
      });

      currentOpacityRef.current = opacity;

      setCurrentOpacity(opacity);
    },
    [setCurrentOpacity, contentBoard.editor]
  );

  const handleTool = useCallback(
    (tool: ContentBoardAvailablePanelTool) => {
      if (!contentBoard.editor) {
        return;
      }

      try {
        contentBoard.editor.setSelectedShapes([]);

        setSelectionHasFill(false);

        setCurrentTool(tool);

        currentToolRef.current = tool;

        if (tool === 'ellipse' || tool === 'rectangle') {
          selectGeo(tool);
          return;
        }

        if (contentBoard.editor.currentToolId !== tool) {
          contentBoard.editor.setCurrentTool(CONTENT_BOARD_AVAILABLE_TOOLS_MAP[tool]);
        }

        contentBoard.editor.setOpacityForNextShapes(
          currentToolRef.current === 'highlight'
            ? Math.max(currentOpacityRef.current * 0.5, 0)
            : currentOpacityRef.current
        );
      } catch (error) {
        console.log(`This tool is unavailable (${tool}): `, error);
      }
    },
    [selectGeo, setCurrentTool, contentBoard.editor]
  );

  const handleUndo = useCallback(() => {
    if (!contentBoard.editor) {
      return;
    }

    contentBoard.editor.undo();
  }, [contentBoard.editor]);

  const handleRedo = useCallback(() => {
    if (!contentBoard.editor) {
      return;
    }

    contentBoard.editor.redo();
  }, [contentBoard.editor]);

  const handleClearAll = useCallback(() => {
    if (!contentBoard.editor) {
      return;
    }

    try {
      contentBoard.editor.batch(() => {
        if (!contentBoard.editor) {
          return;
        }

        const shapes: any[] = contentBoard.editor.currentPageShapes;
        const idsToDelete = shapes
          .map((s: any) => s?.id)
          .filter((id: any) => id && id !== CONTENT_BOARD_BG_SHAPE_ID);

        if (idsToDelete.length) {
          contentBoard.editor.deleteShapes(idsToDelete);
        }

        contentBoard.editor.setSelectedShapes([]);
      });
    } catch {
      /* do nothing */
    }
  }, [contentBoard.editor]);

  useEffect(() => {
    if (!currentEditor) {
      return;
    }

    handleColor(currentColorRef.current);
    handleOpacity(currentOpacityRef.current);
    handleFill(currentFillRef.current);
    handleTool(currentToolRef.current);

    currentEditor.on('change', (change) => {
      contentBoard.onLaserPointerUpdate(change);

      const currentToolId = currentEditor?.currentToolId;
      const currentEditorTool = CONTENT_BOARD_TOOLS_MAP[currentToolId as ContentBoardTool];
      if (
        currentToolId !== 'geo' &&
        currentEditorTool &&
        currentToolRef.current !== currentEditorTool
      ) {
        setCurrentTool(currentEditorTool);
      }

      if (currentEditor) {
        const selectedEventDataKey = Object.keys(change.changes.updated).find((key) =>
          key.startsWith('instance_page_state:')
        );

        if (selectedEventDataKey) {
          const data = change.changes.updated[selectedEventDataKey as any];
          if (data) {
            const lastItem: any = data[data.length - 1];
            if (lastItem.selectedShapeIds) {
              const selectedShapeIdsString: string = lastItem.selectedShapeIds.reduce(
                (acc: string, id: string) => `${acc}${id},`,
                ''
              );

              if (selectedShapeIdsString !== currentSelectedShapes.current) {
                currentSelectedShapes.current = selectedShapeIdsString;
                const shapes: any[] = currentEditor.selectedShapes;
                if (shapes.length > 0) {
                  const lastSelectedShape = shapes[shapes.length - 1];
                  currentColorRef.current = lastSelectedShape.props.color;
                  setCurrentColor(lastSelectedShape.props.color);

                  const lastFilledShape = [...shapes]
                    .reverse()
                    .find((shape) => CONTENT_BOARD_FILL_SHAPE_TYPES.includes(shape.type));
                  setSelectionHasFill(Boolean(lastFilledShape));
                  if (lastFilledShape) {
                    adoptFill(lastFilledShape.props.fill);
                  }

                  let nextOpacity;
                  if (currentToolRef.current === 'highlight') {
                    nextOpacity =
                      lastSelectedShape.type !== 'highlight'
                        ? Math.max(lastSelectedShape.opacity * 0.5, 0)
                        : lastSelectedShape.opacity;
                    currentOpacityRef.current = nextOpacity;
                    setCurrentOpacity(nextOpacity);
                  } else {
                    nextOpacity =
                      lastSelectedShape.type === 'highlight'
                        ? Math.min(lastSelectedShape.opacity * 2, 1)
                        : lastSelectedShape.opacity;
                    currentOpacityRef.current = nextOpacity;
                    setCurrentOpacity(nextOpacity);
                  }

                  if (contentBoard.editor) {
                    contentBoard.editor.setOpacityForNextShapes(nextOpacity);
                  }
                } else {
                  setCurrentColor(currentColorRef.current);
                  setCurrentOpacity(currentOpacityRef.current);
                  setCurrentFill(currentFillRef.current);
                  setSelectionHasFill(false);
                }
              }
            }
          }
        }
      }
    });

    return () => {
      if (currentEditor) {
        currentEditor.off('change');
      }
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [currentEditor]);

  const renderItem = useCallback(
    (tool: ContentBoardAvailablePanelTool, title: string, onClick?: () => void) => (
      <MenuItem
        onClick={onClick || (() => handleTool(tool))}
        sx={
          currentTool === tool
            ? (theme) => ({
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              })
            : null
        }
      >
        <ListItemIcon>
          <Icon name={CONTENT_BOARD_PANEL_ICONS_MAP[tool]} />
        </ListItemIcon>
        <ListItemText primary={title} />
      </MenuItem>
    ),
    [handleTool, currentTool]
  );

  const showFill = selectionHasFill || CONTENT_BOARD_FILL_TOOLS.includes(currentTool);

  if (pipWindow) {
    return null;
  }

  return (
    <Box className={!isMobile && !open ? 'hover-child' : ''} sx={{ pointerEvents: 'auto' }}>
      <Menu
        id={stylePanelId}
        open={open}
        onClose={closeMenu}
        closeOnItemClick={false}
        placement="top"
        PopperProps={{
          disablePortal: true,
          sx: {
            minWidth: 240,
            maxWidth: 'calc(100vw - 100px)',
            maxHeight: 'calc(100dvh - 220px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            borderRadius: 1,
          },
        }}
        trigger={
          <Box>
            <Tooltip title={tooltip}>
              <ControlContainer onClick={toggleMenu} aria-label={tooltip}>
                <Icon
                  name={CONTENT_BOARD_PANEL_ICONS_MAP[currentTool]}
                  sx={{ fontSize: 24, color: 'white ' }}
                />
                <Icon
                  name="opened-folder-arrow"
                  sx={(theme) => ({
                    fontSize: 30,
                    color: 'white',
                    transition: theme.transitions.create('transform'),
                    transform: !open ? 'rotate(-180deg)' : 'rotate(0deg)',
                  })}
                />
              </ControlContainer>
            </Tooltip>
          </Box>
        }
      >
        {renderItem('laser', t('tools.highlight_pointer'))}
        {renderItem('cursor', t('actions.select'))}
        {renderItem('pen', t('tools.pen'))}
        {renderItem('highlight', t('tools.highlighter'))}
        {renderItem('text', t('tools.textBox'))}
        {renderItem('note', t('tools.sticky_note'))}
        <Divider sx={{ my: 1 }} />
        {renderItem('ellipse', t('tools.circle'))}
        {renderItem('rectangle', t('tools.rectangle'))}
        {renderItem('line', t('tools.line'))}
        {renderItem('arrow', t('tools.arrow'))}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ mt: '10px', mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              flexWrap: 'wrap',
              rowGap: '2px',
              mb: 1,
            }}
          >
            {CONTENT_BOARD_COLORS.map((color) => (
              <Box
                key={color}
                sx={{
                  width: '20%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  onClick={() => handleColor(color)}
                  sx={(theme) => ({
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor:
                      currentColor === color
                        ? alpha(theme.palette.common.black, 0.04)
                        : 'transparent',
                    '&:hover': {
                      opacity: '0.8',
                      backgroundColor: alpha(theme.palette.common.black, 0.02),
                    },
                  })}
                >
                  <Box
                    sx={{
                      width: 15,
                      height: 15,
                      borderRadius: '50%',
                      backgroundColor: CONTENT_BOARD_COLORS_MAP[color],
                      outline: '2px solid white',
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ px: 4 }}>
            <Slider
              size="small"
              color="primary"
              defaultValue={1}
              value={currentOpacity}
              aria-label={t('panel.opacity')}
              min={0.2}
              max={1}
              step={0.2}
              onChange={(_, value) => {
                _.preventDefault();
                _.stopPropagation();
                handleOpacity(value);
              }}
            />
          </Box>
          {showFill && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around',
                mt: -1,
                mb: 3,
              }}
            >
              {CONTENT_BOARD_FILLS.map((fill) => (
                <Tooltip key={fill} title={t(`fill.${fill}`)}>
                  <IconButton
                    aria-label={t(`fill.${fill}`)}
                    aria-pressed={currentFill === fill}
                    onClick={() => handleFill(fill)}
                    sx={(theme) => ({
                      width: 30,
                      height: 30,
                      backgroundColor:
                        currentFill === fill
                          ? alpha(theme.palette.primary.main, 0.1)
                          : 'transparent',
                    })}
                  >
                    <Icon name={CONTENT_BOARD_FILL_ICONS_MAP[fill]} sx={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>
        <Divider sx={{ mb: 1, mt: 0 }} />
        <MenuItem onClick={() => handleUndo()}>
          <ListItemIcon>
            <Icon name={CONTENT_BOARD_PANEL_ICONS_MAP.undo} />
          </ListItemIcon>
          <ListItemText primary={t('actions.undo')} />
        </MenuItem>
        <MenuItem onClick={() => handleRedo()}>
          <ListItemIcon>
            <Icon name={CONTENT_BOARD_PANEL_ICONS_MAP.redo} />
          </ListItemIcon>
          <ListItemText primary={t('actions.redo')} />
        </MenuItem>
        {renderItem('eraser', t('tools.eraser'))}
        <MenuItem onClick={() => handleClearAll()}>
          <ListItemIcon>
            <Icon name={CONTENT_BOARD_PANEL_ICONS_MAP.clear} />
          </ListItemIcon>
          <ListItemText primary={t('actions.clear_all')} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ContentWhiteboardPanelControl;
